import {
  ArrowLeft,
  CalendarClock,
  FlaskConical,
  LineChart,
  NotebookPen,
  PawPrint,
  Pill,
  Stethoscope,
} from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  useHistoricoControllerLinhaDoTempo,
  useHistoricoControllerResumo,
} from '@/api/generated/historico/historico';
import { AbaEspecialistaAssociado } from '@/features/historico/components/AbaEspecialistaAssociado';
import { AbaEvolucao } from '@/features/historico/components/AbaEvolucao';
import { AbaExames } from '@/features/historico/components/AbaExames';
import { AbaProntuario } from '@/features/historico/components/AbaProntuario';
import { AbaTratamentos } from '@/features/historico/components/AbaTratamentos';
import { LinhaDoTempo } from '@/features/historico/components/LinhaDoTempo';
import { BotaoFavorito } from '@/features/pacientes/components/BotaoFavorito';
import { ESPECIES, formatarIdade } from '@/features/pacientes/lib/especies';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

/** Ordem das abas, e os valores aceitos em `?aba=`. */
const ABAS = [
  'exames',
  'prontuario',
  'tratamentos',
  'linha-do-tempo',
  'evolucao',
  'especialista-associado',
] as const;
type Aba = (typeof ABAS)[number];

/**
 * Histórico clínico do paciente (F6-1).
 *
 * As quatro visões da V1, mais Conexão com especialista, num lugar só. A V1
 * trocava as abas por `?view=` e era assim que o card do paciente abria
 * direto no prontuário (`Historico?paciente=X&view=prontuario`); aqui o
 * parâmetro é `?aba=`, e o mesmo deep-link continua funcionando.
 *
 * ⚠️ Só a aba de prontuário escreve. As outras são leitura.
 */
export function HistoricoPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [busca, setBusca] = useSearchParams();

  const pedida = busca.get('aba');
  const aba: Aba = ABAS.includes(pedida as Aba) ? (pedida as Aba) : 'linha-do-tempo';

  // `replace` para a troca de aba não empilhar histórico do browser: voltar
  // deve sair da tela, não desfazer cliques de aba um a um.
  function trocarAba(nova: string) {
    setBusca({ aba: nova }, { replace: true });
  }

  const resumo = useHistoricoControllerResumo(id);
  const linha = useHistoricoControllerLinhaDoTempo(id, { limite: 50 });

  if (resumo.isPending) return <EstadoCarregando rotulo="Carregando histórico..." />;

  if (resumo.isError || !resumo.data) {
    return (
      <EstadoErro
        aoTentarDeNovo={() => void resumo.refetch()}
        titulo="Não foi possível abrir o histórico"
        descricao="O paciente pode ter sido removido, ou não pertence a este ambiente."
      />
    );
  }

  const paciente = resumo.data;

  return (
    <div className="space-y-6">
      {/* `print:hidden`: a Evolução imprime seu próprio cabeçalho (paciente +
          logo), então o breadcrumb/badges daqui embaixo não fazem sentido no
          papel — ver `AbaEvolucao`. */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 print:hidden">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-1">
            <Link to={ROTAS.pacientes}>
              <ArrowLeft className="w-4 h-4" />
              Pacientes
            </Link>
          </Button>

          <h2 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            {/* A silhueta continua sendo o padrão: nem todo paciente tem foto, e
                a URL assinada expira. Mesma queda do `PacienteCard`. */}
            {paciente.fotoUrl ? (
              <img
                src={paciente.fotoUrl}
                alt={paciente.pacienteNome}
                className="w-9 h-9 rounded-lg object-cover border border-brand/20"
              />
            ) : (
              <PawPrint className="w-6 h-6 text-brand" aria-hidden="true" />
            )}
            {paciente.pacienteNome}
            {/* A mesma estrela da lista. Quem chega aqui pelo resultado de uma
                busca é justamente quem quer marcar o paciente para não ter de
                buscar de novo amanhã. */}
            <BotaoFavorito
              paciente={{
                id: paciente.pacienteId,
                nome: paciente.pacienteNome,
                favorito: paciente.favorito,
              }}
            />
          </h2>

          <p className="text-sm text-muted-foreground mt-0.5">
            {[
              ESPECIES[paciente.especie as keyof typeof ESPECIES] ?? paciente.especie,
              formatarIdade(paciente.idadeMeses),
              paciente.tutorNome,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="font-normal">
            {paciente.totalExames} exames
          </Badge>
          <Badge variant="outline" className="font-normal">
            {paciente.totalProntuarios} prontuários
          </Badge>
          <Badge variant="outline" className="font-normal">
            {paciente.totalRequisicoes} requisições
          </Badge>
          <Badge variant="outline" className="font-normal">
            {paciente.totalReceituarios} receituários
          </Badge>
        </div>
      </div>

      <Tabs value={aba} onValueChange={trocarAba}>
        <TabsList className="print:hidden">
          <TabsTrigger value="exames">
            <FlaskConical className="w-4 h-4" />
            Exames
          </TabsTrigger>
          <TabsTrigger value="prontuario">
            <NotebookPen className="w-4 h-4" />
            Prontuário
          </TabsTrigger>
          <TabsTrigger value="tratamentos">
            <Pill className="w-4 h-4" />
            Tratamentos
          </TabsTrigger>
          <TabsTrigger value="linha-do-tempo">
            <CalendarClock className="w-4 h-4" />
            Linha do tempo
          </TabsTrigger>
          <TabsTrigger value="evolucao">
            <LineChart className="w-4 h-4" />
            Evolução
          </TabsTrigger>
          <TabsTrigger value="especialista-associado">
            <Stethoscope className="w-4 h-4" />
            Conexão com especialista
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exames" className="mt-4">
          <AbaExames pacienteId={id} />
        </TabsContent>

        <TabsContent value="prontuario" className="mt-4">
          <AbaProntuario pacienteId={id} />
        </TabsContent>

        <TabsContent value="tratamentos" className="mt-4">
          <AbaTratamentos pacienteId={id} />
        </TabsContent>

        <TabsContent value="linha-do-tempo" className="mt-4">
          <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6">
            {linha.isPending ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-16 rounded-md" />
                ))}
              </div>
            ) : linha.isError || !linha.data ? (
              <EstadoErro aoTentarDeNovo={() => void linha.refetch()} />
            ) : linha.data.itens.length === 0 ? (
              <EstadoVazio
                icone={CalendarClock}
                titulo="Nada registrado ainda"
                descricao="Exames, prontuários, requisições e receituários deste paciente aparecem aqui."
              />
            ) : (
              <LinhaDoTempo eventos={linha.data.itens} />
            )}
          </div>
        </TabsContent>

        <TabsContent value="evolucao" className="mt-4">
          {paciente.analitos.length === 0 ? (
            <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
              <EstadoVazio
                icone={LineChart}
                titulo="Nenhum resultado lançado"
                descricao="A evolução aparece quando houver exames concluídos com resultados."
              />
            </div>
          ) : (
            <AbaEvolucao
              pacienteId={id}
              analitos={paciente.analitos}
              paciente={{
                nome: paciente.pacienteNome,
                especie: ESPECIES[paciente.especie as keyof typeof ESPECIES] ?? paciente.especie,
                idade: formatarIdade(paciente.idadeMeses),
                tutorNome: paciente.tutorNome,
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="especialista-associado" className="mt-4">
          <AbaEspecialistaAssociado pacienteId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
